import { lastMonths, parsePlainDate } from '../dates/index.js';
import { cents } from '../money/index.js';
import { failure, success, type AwardTypeCode, type Cents, type Result } from '../types.js';
import { amountIssue } from '../validation.js';

export interface RecognitionAward {
  readonly awardId: string;
  readonly personId: string;
  readonly awardDate: string;
  readonly type: AwardTypeCode;
  readonly valueCents: number;
  readonly status: 'awarded' | 'pending' | 'rejected' | 'cancelled';
}
export interface RepeatSettings {
  readonly awardCount: number;
  readonly valueCents: number;
  readonly rollingMonths: number;
}
export interface RecipientRecognition {
  readonly personId: string;
  readonly awardCount: number;
  readonly valueCents: Cents;
  readonly repeat: boolean;
}
const defaults: RepeatSettings = { awardCount: 3, valueCents: 500000, rollingMonths: 12 };
/** Q18: count OR value. K37: Spot is excluded. The window is the rule's months counted as every
 * report counts the last N months (APP-199): whole calendar months, from the 1st of the month
 * N − 1 back, to `asOf`, both ends inclusive. The adapter supplies awarded-status records and their
 * persisted award values. */
export function repeatRecipients(
  awards: readonly RecognitionAward[],
  asOf: string,
  settings: RepeatSettings = defaults,
): Result<readonly RecipientRecognition[]> {
  if (!parsePlainDate(asOf).ok) return failure('invalid_date');
  if (
    ![settings.awardCount, settings.rollingMonths].every(
      (value) => Number.isSafeInteger(value) && value > 0,
    ) ||
    settings.rollingMonths > 1200 ||
    amountIssue(settings.valueCents, 'valueCents', true).length
  )
    return failure('invalid_repeat_settings');
  const window = lastMonths(asOf, settings.rollingMonths);
  if (!window.ok) return failure('invalid_date_range');
  const start = window.value.from;
  const seen = new Set<string>(),
    recipients = new Map<string, { count: number; value: bigint }>();
  for (const award of awards) {
    if (
      !award.awardId.trim() ||
      !award.personId.trim() ||
      !parsePlainDate(award.awardDate).ok ||
      amountIssue(award.valueCents, 'valueCents').length
    )
      return failure('invalid_recognition_line');
    const key = JSON.stringify([award.awardId, award.personId]);
    if (seen.has(key)) return failure('duplicate_recognition_line');
    seen.add(key);
    if (
      award.type === 'SPOT' ||
      award.status !== 'awarded' ||
      award.awardDate < start ||
      award.awardDate > asOf
    )
      continue;
    const old = recipients.get(award.personId) ?? { count: 0, value: 0n };
    recipients.set(award.personId, {
      count: old.count + 1,
      value: old.value + BigInt(award.valueCents),
    });
  }
  const result: RecipientRecognition[] = [];
  for (const [personId, data] of recipients) {
    if (data.value > BigInt(Number.MAX_SAFE_INTEGER)) return failure('amount_overflow');
    result.push({
      personId,
      awardCount: data.count,
      valueCents: cents(Number(data.value)),
      repeat: data.count >= settings.awardCount || data.value >= BigInt(settings.valueCents),
    });
  }
  return success(
    result.sort((a, b) => (a.personId < b.personId ? -1 : a.personId > b.personId ? 1 : 0)),
  );
}
export interface CoverageCount {
  readonly people: number;
  readonly recognized: number;
  readonly repeat: number;
  readonly percent: number | null;
}
/** Returns percents at any group size, for calculations and tests only: never for display (APP-197). */
export function recognitionCoverage(
  roster: readonly { readonly personId: string; readonly sectorId: string | null }[],
  awards: readonly RecognitionAward[],
  asOf: string,
  settings: RepeatSettings = defaults,
): Result<{
  readonly company: CoverageCount;
  readonly sectors: readonly (CoverageCount & { readonly sectorId: string | null })[];
}> {
  if (
    roster.some((person) => !person.personId.trim()) ||
    new Set(roster.map((person) => person.personId)).size !== roster.length
  )
    return failure('invalid_roster');
  const summary = repeatRecipients(awards, asOf, settings);
  if (!summary.ok) return summary;
  const recognized = new Map(summary.value.map((person) => [person.personId, person]));
  const buckets = new Map<string | null, { people: number; recognized: number; repeat: number }>();
  const company = { people: 0, recognized: 0, repeat: 0 };
  for (const person of roster) {
    const bucket = buckets.get(person.sectorId) ?? { people: 0, recognized: 0, repeat: 0 };
    for (const count of [bucket, company]) {
      count.people++;
      if (recognized.has(person.personId)) count.recognized++;
      if (recognized.get(person.personId)?.repeat) count.repeat++;
    }
    buckets.set(person.sectorId, bucket);
  }
  const percent = (count: typeof company): number | null =>
    count.people ? Math.round((count.recognized / count.people) * 100) : null;
  return success({
    company: { ...company, percent: percent(company) },
    sectors: [...buckets].map(([sectorId, count]) => ({
      sectorId,
      ...count,
      percent: percent(count),
    })),
  });
}

/** A whole percent, rounded half up; null when there's nothing to divide by. */
export function percentOf(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.floor((part * 200 + whole) / (2 * whole));
}

/**
 * Groups smaller than this show counts ("2 of 3"), not a percent that overstates a tiny group. One
 * size for Insights and Ask the data (APP-192, AIE-051). Insights applies it to recognition
 * coverage; Ask the data to every rate over a group's people (share recognized, the company
 * average, awards per 100 people).
 */
export const SMALL_GROUP = 5;

/**
 * The words for a rate held back for a small group (APP-197): in a table cell or an export, beside
 * the group's counts.
 */
export const SMALL_GROUP_WORDS = `Fewer than ${String(SMALL_GROUP)} people`;

/** Whether a group of this many people is too small for a rate over its people. */
export function smallGroup(people: number): boolean {
  return people < SMALL_GROUP;
}

/**
 * A chart series' colour slot when the series is an award type (APP-191): each type keeps its own
 * colour on every chart, whatever its rank or what else is drawn, so a type is never two colours
 * across answers. Slot 6 is for a sixth category that isn't a type. The server puts the slot on
 * the series; the web draws `--chart-series-<slot>`.
 */
export type SeriesSlot = 1 | 2 | 3 | 4 | 5 | 6;
const AWARD_TYPE_SLOT: Readonly<Record<AwardTypeCode, SeriesSlot>> = {
  SDB: 1,
  PAA: 2,
  TAA: 3,
  SPOT: 4,
  EDB: 5,
};
export function slotForAwardType(type: AwardTypeCode): SeriesSlot {
  return AWARD_TYPE_SLOT[type];
}
